import { HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import type { HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { CaptureStorage } from './capture-storage';

@Injectable()
export class CaptureInterceptor implements HttpInterceptor {
  constructor(private captureStorage: CaptureStorage) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const startTime = Date.now();

    return next.handle(request).pipe(
      tap({
        next: (event) => {
          if (!(event instanceof HttpResponse)) {
            return;
          }

          this.captureStorage.saveCapture({
            timestamp: new Date().toISOString(),
            method: request.method,
            url: request.url,
            requestHeaders: this.headersToObject(request.headers),
            requestBody: request.body,
            responseStatus: event.status,
            responseHeaders: this.headersToObject(event.headers),
            responseBody: event.body,
            duration: Date.now() - startTime,
          });
        },
        error: (error) => {
          const sanitizedError = this.sanitizeError(error);

          this.captureStorage.saveCapture({
            timestamp: new Date().toISOString(),
            method: request.method,
            url: request.url,
            requestHeaders: this.headersToObject(request.headers),
            requestBody: request.body,
            responseStatus: error.status || 0,
            responseHeaders: error.headers ? this.headersToObject(error.headers) : {},
            responseBody: sanitizedError,
            duration: Date.now() - startTime,
            isError: true,
          }).catch((storageError) => {
            console.warn('[Capture] Failed to save error capture:', storageError);
          });
        },
      })
    );
  }

  private headersToObject(headers: HttpHeaders): Record<string, string> {
    const obj: Record<string, string> = {};
    headers.keys().forEach((key: string) => {
      const value = headers.get(key);
      if (value) {
        obj[key] = value;
      }
    });
    return obj;
  }

  private sanitizeError(error: any): any {
    // Handle ProgressEvent (CORS errors, network failures)
    if (error instanceof ProgressEvent) {
      return {
        type: error.type,
        message: 'Network error or CORS issue',
        loaded: error.loaded,
        total: error.total,
      };
    }

    // Handle HttpErrorResponse with nested ProgressEvent
    if (error.error instanceof ProgressEvent) {
      return {
        type: error.error.type,
        message: 'Network error or CORS issue',
        status: error.status,
        statusText: error.statusText,
        url: error.url,
      };
    }

    // Try to return the error as-is if it's already serializable
    try {
      JSON.stringify(error.error || error);
      return error.error || error;
    } catch {
      // If it can't be serialized, return a string representation
      return {
        message: error.message || String(error),
        status: error.status,
        statusText: error.statusText,
        url: error.url,
      };
    }
  }
}
